import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SupervisorLayout from '../../components/SupervisorLayout';
import SupervisorBankCodeField from '../../components/SupervisorBankCodeField';
import { bulkSupervisorBankStatus, fetchSupervisorBankStatusList } from '../../api/supervisor';
import './SupervisorActiverBanqueManuelPage.css';

// Variante superviseur (2026-09-17) de pages/parametres/ActiverBanqueManuelPage.jsx
// — une seule banque a la fois, parmi SES banks (portee Groupe retiree le
// 2026-09-21, portee "Tout" retiree le 2026-09-23 a la demande de l'utilisateur) ;
// meme UI texte + icone 🔍 + modal + resolution au blur (SupervisorBankCodeField),
// pas de BankScopeField company-wide.
const ACTION_OPTIONS = [
  { value: 'unblock_sale', label: 'Débloquer Vente' },
  { value: 'block_sale', label: 'Bloquer Vente' },
  { value: 'unblock_login', label: 'Débloquer Banque (connexion)' },
  { value: 'block_login', label: 'Bloquer Banque (connexion)' },
];

const ACTION_HINTS = {
  block_sale: "Le bank reste connectable et peut toujours consulter ses rapports — seule la création de fiches sera refusée.",
  unblock_sale: "Autorise à nouveau la création de fiches pour ce scope.",
  block_login: "Le bank ne pourra plus se connecter du tout à l'app (même pour consulter ses rapports).",
  unblock_login: "Autorise à nouveau la connexion à l'app pour ce scope.",
};

const SupervisorActiverBanqueManuelPage = () => {
  const [scopeId, setScopeId] = useState('');
  const [scopeName, setScopeName] = useState('');
  const [action, setAction] = useState('block_sale');

  const [busy, setBusy] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [rows, setRows] = useState(null);

  const loadList = async () => {
    if (!scopeId) {
      setError('Choisis une banque.');
      return;
    }
    setLoadingList(true);
    setError('');
    try {
      const data = await fetchSupervisorBankStatusList({ filter: 'Bank', scope: scopeId });
      setRows(data);
    } catch (err) {
      setError('Chargement impossible.');
      setRows(null);
    } finally {
      setLoadingList(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadList();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!scopeId) {
      setError('Choisis une banque.');
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await bulkSupervisorBankStatus({
        action,
        filter: 'Bank',
        scope: scopeId,
      });
      setNotice(`${result.updated} banque(s) mise(s) à jour.`);
      await loadList();
    } catch (err) {
      setError('Action impossible.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <SupervisorLayout>
      <p className="svavm-breadcrumb">
        Ruta : <Link to="/supervisor/dashboard" className="svavm-link">Menu</Link>
        {' >> '}
        <Link to="/supervisor/parametres" className="svavm-link">Parametres</Link>
        {' >> '}
        <span className="svavm-current">Act/Desactiver Ventes (Manuel)</span>
      </p>

      <div className="svavm-card">
        <div className="svavm-card-header">
          <span className="svavm-card-title">Act/Desactiver Ventes (Manuel)</span>
        </div>
        <div className="svavm-card-body">
          <div className="svavm-row svavm-scope-row">
            <label className="svavm-label">Banque</label>
            <SupervisorBankCodeField
              value={scopeId}
              name={scopeName}
              onChange={(id) => { setScopeId(id); setScopeName(''); setNotice(''); }}
              onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); }}
              inputClass="svavm-input svavm-input-sm"
              iconClass="svavm-icon-btn"
              nameClass="svavm-copy-name"
            />
          </div>

          <form onSubmit={handleSearch} className="svavm-row">
            <button type="submit" className="svavm-apply-btn" disabled={loadingList}>
              {loadingList ? 'Recherche...' : 'Chercher'}
            </button>
          </form>

          <form onSubmit={submit}>
            <div className="svavm-row">
              <label className="svavm-label">Action</label>
              <select className="svavm-select" value={action} onChange={(e) => setAction(e.target.value)}>
                {ACTION_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>

            <p className="svavm-action-hint">{ACTION_HINTS[action]}</p>

            {error && <p className="svavm-error">{error}</p>}
            {notice && <p className="svavm-ok">{notice}</p>}

            <button type="submit" className="svavm-apply-btn" disabled={busy}>
              {busy ? 'Application...' : 'Appliquer'}
            </button>
          </form>
        </div>
      </div>

      {rows && (
        <div className="svavm-card svavm-results">
          <div className="svavm-card-header">
            <span className="svavm-card-title">Statut des banques</span>
          </div>
          <div className="svavm-card-body">
            {rows.length === 0 ? (
              <p className="svavm-empty">Aucune banque sur cette portée.</p>
            ) : (
              <table className="svavm-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Banque</th>
                    <th>Username</th>
                    <th>Groupe</th>
                    <th>Vente</th>
                    <th>Connexion</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>{r.seq}</td>
                      <td>{r.name}</td>
                      <td>{r.username || '—'}</td>
                      <td>{r.group_name || '—'}</td>
                      <td>
                        <span className={r.sale_enabled ? 'svavm-status-ok' : 'svavm-status-blocked'}>
                          {r.sale_enabled ? 'Active' : 'Bloquée'}
                        </span>
                      </td>
                      <td>
                        <span className={r.login_blocked ? 'svavm-status-blocked' : 'svavm-status-ok'}>
                          {r.login_blocked ? 'Bloquée' : 'Active'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </SupervisorLayout>
  );
};

export default SupervisorActiverBanqueManuelPage;
